import { policyExpiryDate } from "@/lib/leave-expiry";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { differenceInCalendarDays, format, isValid, parseISO, startOfDay } from "date-fns";
import { AlertTriangle, CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDateLocale } from "@/hooks/useDateLocale";
import type { LeaveBalance, LeaveCompanyPolicy } from "@/lib/api";
import { getLeaveTypeLabel } from "@/lib/leave-i18n";

interface AnnualLeaveBalanceSummaryProps {
  balance?: LeaveBalance;
  policy?: LeaveCompanyPolicy;
  className?: string;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}

function getConfiguredExpiryDate(
  year: number,
  month: number | null,
  day: number | null,
): Date | null {
  if (month == null || day == null) return null;
  const iso = policyExpiryDate(year + 1, month, day);
  if (!iso) return null;
  const date = parseISO(iso);
  if (
    !isValid(date) ||
    date.getFullYear() !== year + 1 ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== Number(iso.slice(-2))
  ) {
    return null;
  }
  return date;
}

export function AnnualLeaveBalanceSummary({
  balance,
  policy,
  className,
  loading = false, error = false, onRetry,
}: AnnualLeaveBalanceSummaryProps) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  if (loading || error || !balance) {
    return <Card className={className ?? "shadow-card"}><CardHeader><CardTitle className="text-base">{t("profile.timeOff.balanceTitle")}</CardTitle></CardHeader>
      <CardContent aria-busy={loading}>
        {loading ? <div className="grid grid-cols-3 gap-3" role="status" aria-label={t("common.loading")}>
          {[0, 1, 2].map(i => <div key={i} className="rounded-lg bg-muted/50 p-3"><Skeleton className="h-3 w-16 mb-3" /><Skeleton className="h-7 w-12" /></div>)}
        </div> : <div role="alert" className="text-sm text-muted-foreground"><p>{t("profile.timeOff.balanceError")}</p>
          {onRetry && <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">{t("common.retry")}</Button>}</div>}
      </CardContent></Card>;
  }
  const year = balance?.year ?? new Date().getFullYear();
  const totalDays = Number(balance?.totalDays ?? 0);
  const usedDays = Number(balance?.usedDays ?? 0);
  const pendingDays = Number(balance?.pendingDays ?? 0);
  const carryOverDays = Number(balance?.carryOverDays ?? 0);
  const remainingDays = Number(
    balance?.availableNow ?? Math.max(0, totalDays + carryOverDays - usedDays - pendingDays),
  );
  const currentYearRemaining = Math.max(0, totalDays - usedDays - pendingDays);
  const maxCarryOver = Math.max(0, Number(policy?.annualLeaveCarryOverMax ?? 0));
  const eligibleToCarry = Math.min(currentYearRemaining, maxCarryOver);
  const useByYearEnd = Math.max(0, currentYearRemaining - eligibleToCarry);
  const yearEnd = parseISO(`${year}-12-31`);
  const configuredExpiry = policy
    ? getConfiguredExpiryDate(
        year,
        policy.annualLeaveExpiryMonth,
        policy.annualLeaveExpiryDay,
      )
    : null;
  const actualCarryExpiry = balance?.carryOverExpiry
    ? parseISO(balance.carryOverExpiry.slice(0, 10))
    : null;
  const validActualCarryExpiry = actualCarryExpiry && isValid(actualCarryExpiry)
    ? actualCarryExpiry
    : null;
  const daysUntilCarryExpiry = validActualCarryExpiry
    ? differenceInCalendarDays(validActualCarryExpiry, startOfDay(new Date()))
    : null;
  const carryOverApproachingExpiry =
    remainingDays > 0 &&
    carryOverDays > 0 &&
    daysUntilCarryExpiry != null &&
    daysUntilCarryExpiry >= 0 &&
    daysUntilCarryExpiry <= 60;
  const dayCount = (count: number) => t("profile.timeOff.dayCount", { count });
  const dateLabel = (date: Date) => format(date, "P", { locale: dateLocale });

  return (
    <Card className={className ?? "shadow-card"}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <CalendarClock className="h-4 w-4 text-primary" />
          {t("profile.timeOff.annualSummaryTitle", {
            year,
            leaveType: getLeaveTypeLabel(
              t,
              balance?.leaveType ?? "annual",
              balance?.leaveTypeLabel,
            ),
          })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className={`grid gap-2 ${pendingDays > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
          <div className="rounded-lg bg-muted/50 px-3 py-2.5">
            <p className="text-[11px] text-muted-foreground">{t("profile.timeOff.entitlement")}</p>
            <p className="mt-0.5 text-lg font-semibold">{dayCount(totalDays)}</p>
          </div>
          <div className="rounded-lg bg-muted/50 px-3 py-2.5">
            <p className="text-[11px] text-muted-foreground">{t("leave.approvedDaysIncludingUpcoming")}</p>
            <p className="mt-0.5 text-lg font-semibold">{dayCount(usedDays)}</p>
          </div>
          {pendingDays > 0 && (
            <div className="rounded-lg bg-amber-500/10 px-3 py-2.5">
              <p className="text-[11px] text-amber-700 dark:text-amber-300">{t("profile.timeOff.pendingDays")}</p>
              <p className="mt-0.5 text-lg font-semibold text-amber-700 dark:text-amber-300">
                {dayCount(pendingDays)}
              </p>
            </div>
          )}
          <div className="rounded-lg bg-primary/10 px-3 py-2.5 ring-1 ring-primary/15">
            <p className="text-[11px] font-medium text-primary">{t("profile.timeOff.remainingDays")}</p>
            <p className="mt-0.5 text-xl font-bold text-primary">{dayCount(remainingDays)}</p>
          </div>
        </div>

        {remainingDays > 0 && carryOverDays > 0 && (
          <div className="rounded-lg border border-border/60 px-3 py-2.5 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-muted-foreground">
                {t("profile.timeOff.carriedFrom", { year: year - 1 })}
              </span>
              <span className="font-semibold">{dayCount(carryOverDays)}</span>
            </div>
            {validActualCarryExpiry && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t("profile.timeOff.carriedUseBy", { date: dateLabel(validActualCarryExpiry) })}
              </p>
            )}
          </div>
        )}

        {remainingDays > 0 && currentYearRemaining > 0 && policy && (
          <div className="grid gap-x-4 gap-y-2 border-t border-border/60 pt-3 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("profile.timeOff.maxCarryOver")}</span>
              <span className="font-medium">{dayCount(maxCarryOver)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">{t("profile.timeOff.canCarryOver")}</span>
              <span className="font-medium">{dayCount(eligibleToCarry)}</span>
            </div>
            <div className="flex justify-between gap-3 sm:col-span-2">
              <span className="text-muted-foreground">
                {t("profile.timeOff.useBy", { date: dateLabel(yearEnd) })}
              </span>
              <span className={useByYearEnd > 0 ? "font-semibold text-amber-700 dark:text-amber-300" : "font-medium"}>
                {dayCount(useByYearEnd)}
              </span>
            </div>
            {eligibleToCarry > 0 && configuredExpiry && (
              <p className="text-xs text-muted-foreground sm:col-span-2">
                {t("profile.timeOff.futureCarryExpiry", { date: dateLabel(configuredExpiry) })}
              </p>
            )}
          </div>
        )}

        {policy && remainingDays > 0 && useByYearEnd > 0 && (
          <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              {maxCarryOver === 0
                ? t("profile.timeOff.noCarryOverWarning", {
                    count: useByYearEnd,
                    date: dateLabel(yearEnd),
                  })
                : t("profile.timeOff.exceedsCarryOverWarning", {
                    count: useByYearEnd,
                    date: dateLabel(yearEnd),
                  })}
            </p>
          </div>
        )}

        {carryOverApproachingExpiry && validActualCarryExpiry && (
          <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              {t("profile.timeOff.carryOverExpiryWarning", {
                count: carryOverDays,
                date: dateLabel(validActualCarryExpiry),
              })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
